// ═══════════════════════════════════════════════════════════════
// Incident Hub — All Incidents List
// Filterable by status, searchable, sortable
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, SegmentedControl, Stack,
} from '../../components/gab/GabComponents';
import {
  IconSearch, IconPlus, IconCloudStorm, IconFlame, IconBiohazard,
  IconClipboardCheck, IconSchool, IconInfoCircle, IconDroplet,
  IconBuildingBridge, IconTornado, IconCalendar, IconShield, IconAmbulance, IconConfetti,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { INCIDENT_TYPE_META } from '../../types';
import { secondary, semantic } from '../../lib/colors';

const STATUS_COLORS: Record<string, string> = {
  active: semantic.warning,
  monitoring: semantic.info,
  closed: semantic.success,
  archived: secondary[700],
};

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  IconCloudStorm, IconFlame, IconBiohazard, IconClipboardCheck,
  IconSchool, IconInfoCircle, IconDroplet, IconBuildingBridge,
  IconTornado, IconCalendar, IconShield, IconAmbulance, IconConfetti,
};

export default function IncidentList() {
  const navigate = useNavigate();
  const { incidents, loadIncidents } = useAuthStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { loadIncidents(); }, []);

  const statusCounts = useMemo(() => ({
    all: incidents.length,
    active: incidents.filter(i => i.status === 'active').length,
    monitoring: incidents.filter(i => i.status === 'monitoring').length,
    closed: incidents.filter(i => i.status === 'closed').length,
  }), [incidents]);

  const filtered = useMemo(() => {
    let list = incidents;
    if (statusFilter !== 'all') list = list.filter(i => i.status === statusFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(i =>
        (i.name || '').toLowerCase().includes(q) ||
        (i.commander || '').toLowerCase().includes(q) ||
        (i.location || '').toLowerCase().includes(q) ||
        (i.incidentType || '').toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      // Active first, then monitoring, then closed, then by date
      const statusOrder: Record<string, number> = { active: 0, monitoring: 1, closed: 2, archived: 3 };
      const diff = (statusOrder[a.status] ?? 4) - (statusOrder[b.status] ?? 4);
      if (diff !== 0) return diff;
      return (b.datecreated || '').localeCompare(a.datecreated || '');
    });
  }, [incidents, statusFilter, search]);

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">All Incidents</Title>
        <Button size="xs" leftSection={<IconPlus size={14} />}
          onClick={() => navigate('/incidents/new')}>New Incident</Button>
      </Group>

      <Group mb="md" gap="sm" align="flex-end">
        <TextInput placeholder="Search name, commander, location, type..."
          leftSection={<IconSearch size={14} />}
          value={search} onChange={e => setSearch(e.target.value)}
          size="xs" style={{ flex: 1, minWidth: 200 }} />
      </Group>

      <SegmentedControl
        value={statusFilter}
        onChange={setStatusFilter}
        size="xs"
        mb="md"
        data={[
          { value: 'all', label: `All (${statusCounts.all})` },
          { value: 'active', label: `Active (${statusCounts.active})` },
          { value: 'monitoring', label: `Monitoring (${statusCounts.monitoring})` },
          { value: 'closed', label: `Closed (${statusCounts.closed})` },
        ]}
      />

      <Text size="xs" c="dimmed" mb="sm">Showing {filtered.length} incidents</Text>

      {filtered.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder>
          <Text ta="center" c="dimmed">No incidents found.</Text>
        </Paper>
      ) : (
        <Stack gap="xs">
          {filtered.map(inc => {
            const meta = INCIDENT_TYPE_META[inc.incidentType] || INCIDENT_TYPE_META.other;
            const Icon = ICON_MAP[meta.icon] || IconInfoCircle;
            return (
              <Paper key={inc.id} p="sm" radius="sm" withBorder
                style={{ cursor: 'pointer' }} onClick={() => navigate(`/incidents/${inc.id}`)}>
                <Group justify="space-between">
                  <Group gap="sm">
                    <div style={{
                      width: 36, height: 36, borderRadius: 8,
                      background: meta.color + '18', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Icon size={18} color={meta.color} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600} truncate>{inc.name}</Text>
                      <Text size="xs" c="dimmed" truncate>
                        {meta.label} · {inc.location || 'No location'} · {inc.commander ? `IC: ${inc.commander}` : 'No IC'} · Started {inc.startDate || '—'}
                      </Text>
                    </div>
                  </Group>
                  <Group gap="xs" style={{ flexShrink: 0 }}>
                    <Badge variant="light" size="sm" style={{
                      backgroundColor: STATUS_COLORS[inc.status] + '18',
                      color: STATUS_COLORS[inc.status],
                    }}>{inc.status}</Badge>
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </Stack>
      )}
    </div>
  );
}
